import { useState, useEffect, useRef } from 'react';

const navLinks = [
  { label: '首页', href: '/' },
  { label: '体育赛事', href: '/category/sports' },
  { label: '电子竞技', href: '/category/esports' },
  { label: '赛事资讯', href: '/category/news' },
  { label: '关于我们', href: '/about' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const location = useLocation();
  const dropdownRef = useRef<HTMLDivElement>(null);

  const categoryLinks = [
    { label: '足球赛事', href: '/category/football' },
    { label: '篮球赛事', href: '/category/basketball' },
    { label: '网球赛事', href: '/category/tennis' },
    { label: '电子竞技', href: '/category/esports' },
    { label: '综合赛事', href: '/category/other' },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setActiveDropdown(null);
  }, [location.pathname]);

  const isActive = (href: string) => {
    if (href === '/') return location.pathname === '/';
    return location.pathname.startsWith(href);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-18">
          <a href="/" className="flex items-center gap-2 shrink-0">
            <span className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-primary-500">
              KAIYUN
            </span>
          </a>

          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => {
              if (link.href === '/category/sports') {
                return (
                  <div key={link.href} className="relative" ref={dropdownRef}>
                    <button
                      onClick={() => setActiveDropdown(activeDropdown === 'sports' ? null : 'sports')}
                      className={`relative px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer flex items-center gap-1 ${
                        isActive('/category/')
                          ? 'text-primary-600 bg-primary-50'
                          : scrolled
                          ? 'text-foreground-700 hover:text-primary-600 hover:bg-background-100'
                          : 'text-background-50 hover:text-background-50/80'
                      }`}
                    >
                      {link.label}
                      <i className={`ri-arrow-down-s-line text-xs transition-transform duration-200 ${activeDropdown === 'sports' ? 'rotate-180' : ''}`}></i>
                    </button>
                    {activeDropdown === 'sports' && (
                      <div className="absolute top-full left-0 mt-1 w-44 bg-background-50 rounded-lg shadow-lg border border-background-200 py-1 animate-[fadeIn_0.15s_ease-out]">
                        {categoryLinks.map((cl) => (
                          <a
                            key={cl.href}
                            href={cl.href}
                            className={`block px-4 py-2.5 text-sm transition-colors duration-150 whitespace-nowrap ${
                              isActive(cl.href)
                                ? 'text-primary-600 bg-primary-50'
                                : 'text-foreground-700 hover:text-primary-600 hover:bg-background-100'
                            }`}
                          >
                            {cl.label}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }
              return (
                <a
                  key={link.href}
                  href={link.href}
                  className={`relative px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 whitespace-nowrap ${
                    isActive(link.href)
                      ? 'text-primary-600 bg-primary-50'
                      : scrolled
                      ? 'text-foreground-700 hover:text-primary-600 hover:bg-background-100'
                      : 'text-background-50 hover:text-background-50/80'
                  }`}
                >
                  {link.label}
                </a>
              );
            })}
          </div>

          <div className="hidden md:flex items-center gap-3">
            <a
              href="#"
              className="px-5 py-2 rounded-full text-sm font-semibold bg-primary-500 text-background-50 dark:text-foreground-950 hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              立即注册
            </a>
            <a
              href="#"
              className="px-5 py-2 rounded-full text-sm font-semibold border-2 transition-all duration-200 whitespace-nowrap cursor-pointer"
              style={{
                borderColor: scrolled ? undefined : 'rgba(255,255,255,0.6)',
                color: scrolled ? undefined : 'white',
              }}
              className={`px-5 py-2 rounded-full text-sm font-semibold border-2 whitespace-nowrap cursor-pointer ${
                scrolled
                  ? 'border-primary-500 text-primary-600 hover:bg-primary-50'
                  : 'border-background-50/60 text-background-50 hover:bg-background-50/10'
              }`}
            >
              登录
            </a>
          </div>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className={`md:hidden w-10 h-10 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
              scrolled ? 'text-foreground-800' : 'text-background-50'
            }`}
            aria-label="菜单"
          >
            <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>

        {mobileOpen && (
          <div className="md:hidden bg-background-50 rounded-xl shadow-lg border border-background-200 mt-2 p-4 animate-[fadeIn_0.15s_ease-out]">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className={`block px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                  isActive(link.href)
                    ? 'text-primary-600 bg-primary-50'
                    : 'text-foreground-700 hover:bg-background-100'
                }`}
              >
                {link.label}
              </a>
            ))}
            <div className="border-t border-background-200 mt-3 pt-3 flex flex-col gap-2">
              <a href="#" className="block w-full text-center px-5 py-2.5 rounded-full text-sm font-semibold bg-primary-500 text-background-50 dark:text-foreground-950 whitespace-nowrap">
                立即注册
              </a>
              <a href="#" className="block w-full text-center px-5 py-2.5 rounded-full text-sm font-semibold border-2 border-primary-500 text-primary-600 whitespace-nowrap">
                登录
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}